import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";

interface RequireAuthProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

export default function RequireAuth({ children, requireAdmin = false }: RequireAuthProps) {
  const { user, firebaseUser, isAdmin, isLoading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading) {
      if (!user || !firebaseUser) {
        console.log("Auth state: Not authenticated", { user, firebaseUser });
      } else if (requireAdmin && !isAdmin) {
        console.log("Auth state: Not admin", { isAdmin, userRole: user.role });
      } else {
        console.log("Auth state: Authenticated", { isAdmin, userRole: user.role });
      }
    }
  }, [user, firebaseUser, isAdmin, isLoading, requireAdmin]);

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Not authenticated, redirect to login
  if (!user || !firebaseUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has pending email verification
  if (user.verificationData && !user.isEmailVerified) {
    return <Navigate to={`/verify-email?email=${encodeURIComponent(user.email || '')}&name=${encodeURIComponent(user.name || '')}`} replace />;
  }

  // Check admin status if required
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  // User is authenticated (and is admin if required), render children
  return <>{children}</>;
}
